import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '@/App';
import { Target, Plus, Trash2, Save, Eye, Layers, X, Inbox, Check, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { toast } from 'sonner';

const slugify = (s) => s.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '') || `critere_${Date.now()}`;

const CriteriaEditor = ({ criteria, setCriteria, testPrefix }) => {
  const total = criteria.reduce((sum, c) => sum + (parseFloat(c.weight) || 0), 0);
  const update = (idx, field, value) => {
    setCriteria(prev => prev.map((c, i) => (i === idx ? { ...c, [field]: value } : c)));
  };
  return (
    <div className="space-y-2">
      {criteria.map((c, idx) => (
        <div key={idx} className="flex flex-col sm:flex-row gap-2 p-3 rounded-lg bg-white border" data-testid={`${testPrefix}-criterion-row-${idx}`}>
          <div className="flex-1 space-y-1">
            <input
              value={c.label}
              onChange={e => update(idx, 'label', e.target.value)}
              placeholder="Nom du critère"
              className="w-full px-2 py-1.5 text-sm font-medium border rounded-md"
              data-testid={`${testPrefix}-criterion-label-${idx}`}
            />
            <input
              value={c.description || ''}
              onChange={e => update(idx, 'description', e.target.value)}
              placeholder="Description (ce que l'IA doit évaluer)"
              className="w-full px-2 py-1.5 text-xs text-gray-600 border rounded-md"
              data-testid={`${testPrefix}-criterion-desc-${idx}`}
            />
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <input
                type="number" min="0" max="100" step="1"
                value={c.weight}
                onChange={e => update(idx, 'weight', e.target.value === '' ? '' : parseFloat(e.target.value))}
                className="w-16 px-2 py-1.5 text-sm text-center border rounded-md font-semibold"
                data-testid={`${testPrefix}-criterion-weight-${idx}`}
              />
              <span className="text-sm text-gray-500">%</span>
            </div>
            <button
              onClick={() => setCriteria(prev => prev.filter((_, i) => i !== idx))}
              className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50"
              data-testid={`${testPrefix}-criterion-delete-${idx}`}
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
      <div className="flex items-center justify-between pt-1">
        <button
          onClick={() => setCriteria(prev => [...prev, { id: `critere_${Date.now()}`, label: '', description: '', weight: 0 }])}
          className="flex items-center gap-1 text-sm font-medium px-3 py-1.5 rounded-lg border border-dashed border-gray-400 text-gray-600 hover:bg-gray-50"
          data-testid={`${testPrefix}-add-criterion-btn`}
        >
          <Plus className="w-4 h-4" /> Ajouter un critère
        </button>
        <span
          className={`text-sm font-bold px-3 py-1 rounded-full ${Math.abs(total - 100) < 0.01 ? 'bg-green-100 text-green-700' : 'bg-rose-100 text-rose-700'}`}
          data-testid={`${testPrefix}-total-weight`}
        >
          Total : {total.toFixed(0)} % {Math.abs(total - 100) < 0.01 ? '✓' : '(doit être 100 %)'}
        </span>
      </div>
    </div>
  );
};

const EvaluationGridTab = ({ onRequestsChange }) => {
  const navigate = useNavigate();
  const [criteria, setCriteria] = useState([]);
  const [overrides, setOverrides] = useState([]);
  const [scenarios, setScenarios] = useState([]);
  const [protocols, setProtocols] = useState([]);
  const [evaluations, setEvaluations] = useState([]);
  const [requests, setRequests] = useState([]);
  const [requestFilter, setRequestFilter] = useState('pending');
  const [processingId, setProcessingId] = useState(null);
  const [saving, setSaving] = useState(false);
  // Override editor state
  const [overrideTarget, setOverrideTarget] = useState('');
  const [overrideCriteria, setOverrideCriteria] = useState(null);

  const loadAll = useCallback(async () => {
    try {
      const [gridR, ovR, scR, prR, evR, reqR] = await Promise.all([
        axios.get(`${API}/admin/evaluation-grid`),
        axios.get(`${API}/admin/evaluation-grid/overrides`),
        axios.get(`${API}/scenarios`),
        axios.get(`${API}/health/protocols`),
        axios.get(`${API}/admin/auto-evaluations`),
        axios.get(`${API}/admin/evaluation-requests`),
      ]);
      setCriteria(gridR.data.criteria || []);
      setOverrides(ovR.data || []);
      setScenarios((scR.data || []).filter(s => s.category === 'ambulance'));
      setProtocols(prR.data || []);
      setEvaluations(evR.data || []);
      setRequests(reqR.data || []);
    } catch {
      toast.error('Erreur de chargement de la grille');
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const normalizeCriteria = (list) => list.map(c => ({
    ...c,
    id: c.id || slugify(c.label),
    weight: parseFloat(c.weight) || 0,
  }));

  const saveGlobal = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/evaluation-grid`, { criteria: normalizeCriteria(criteria) });
      toast.success('Grille globale sauvegardée');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const findTarget = (id) => {
    const sc = scenarios.find(s => s.id === id);
    if (sc) return { label: sc.title, type: 'scenario' };
    const pr = protocols.find(p => p.id === id);
    if (pr) return { label: pr.title, type: 'protocol' };
    return { label: id, type: 'scenario' };
  };

  const startOverride = () => {
    if (!overrideTarget) { toast.error('Choisissez un scénario ou un protocole'); return; }
    const existing = overrides.find(o => o.target_id === overrideTarget);
    setOverrideCriteria(existing ? existing.criteria.map(c => ({ ...c })) : criteria.map(c => ({ ...c })));
  };

  const saveOverride = async () => {
    const target = findTarget(overrideTarget);
    setSaving(true);
    try {
      await axios.put(`${API}/admin/evaluation-grid/overrides/${overrideTarget}`, {
        criteria: normalizeCriteria(overrideCriteria),
        target_label: target.label,
        target_type: target.type,
      });
      toast.success(`Grille personnalisée sauvegardée pour « ${target.label} »`);
      setOverrideCriteria(null);
      setOverrideTarget('');
      loadAll();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur de sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const deleteOverride = async (targetId) => {
    if (!window.confirm('Supprimer cette grille personnalisée ? La grille globale sera utilisée.')) return;
    try {
      await axios.delete(`${API}/admin/evaluation-grid/overrides/${targetId}`);
      toast.success('Surcharge supprimée');
      loadAll();
    } catch {
      toast.error('Erreur de suppression');
    }
  };

  const approveRequest = async (req) => {
    setProcessingId(req.id);
    try {
      await axios.post(`${API}/admin/evaluation-requests/${req.id}/approve`);
      toast.success(`Évaluation générée pour ${req.student_name || 'l\'étudiant'}`);
      loadAll();
      onRequestsChange?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur lors de la génération');
    } finally {
      setProcessingId(null);
    }
  };

  const refuseRequest = async (req) => {
    const reason = window.prompt('Raison du refus (optionnel) :', '');
    if (reason === null) return;
    setProcessingId(req.id);
    try {
      await axios.post(`${API}/admin/evaluation-requests/${req.id}/refuse`, { reason });
      toast.success('Demande refusée');
      loadAll();
      onRequestsChange?.();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur');
    } finally {
      setProcessingId(null);
    }
  };

  const pendingRequests = requests.filter(r => r.status === 'pending');
  const processedRequests = requests.filter(r => r.status !== 'pending');
  const shownRequests = requestFilter === 'pending' ? pendingRequests : processedRequests;

  const requestTypeBadge = (r) => {
    if (r.request_type === 'retroaction') return { label: 'Rétroaction Incendie', cls: 'bg-orange-50 text-orange-700' };
    if (r.session_type === 'health') return { label: 'Éval. Santé', cls: 'bg-red-50 text-red-700' };
    return { label: 'Éval. Simulateur 911', cls: 'bg-blue-50 text-blue-700' };
  };

  const viewPath = (r) => r.request_type === 'retroaction'
    ? `/retroaction/${r.session_id}`
    : `/auto-evaluation/${r.session_type}/${r.session_id}`;

  return (
    <div className="max-w-7xl mx-auto space-y-6" data-testid="evaluation-grid-tab">
      {/* Evaluation requests */}
      <Card className="glass border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg" style={{ color: '#002768' }}>
            <Inbox className="w-5 h-5" /> Demandes d'évaluation
            {pendingRequests.length > 0 && (
              <span className="w-6 h-6 flex items-center justify-center rounded-full text-xs font-bold text-white" style={{ backgroundColor: '#F59E0B' }} data-testid="pending-requests-badge">
                {pendingRequests.length}
              </span>
            )}
          </CardTitle>
          <CardDescription>
            Les étudiants demandent une évaluation ou une rétroaction. L'appel à l'IA (coût en tokens) se fait uniquement lorsque vous approuvez.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <button
              onClick={() => setRequestFilter('pending')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full ${requestFilter === 'pending' ? 'text-white' : 'bg-white text-gray-600 border'}`}
              style={requestFilter === 'pending' ? { backgroundColor: '#002768' } : {}}
              data-testid="filter-pending-requests"
            >
              En attente ({pendingRequests.length})
            </button>
            <button
              onClick={() => setRequestFilter('processed')}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full ${requestFilter === 'processed' ? 'text-white' : 'bg-white text-gray-600 border'}`}
              style={requestFilter === 'processed' ? { backgroundColor: '#002768' } : {}}
              data-testid="filter-processed-requests"
            >
              Traitées ({processedRequests.length})
            </button>
          </div>

          {shownRequests.length === 0 ? (
            <p className="text-sm text-gray-500 py-2" data-testid="no-requests-msg">
              {requestFilter === 'pending' ? 'Aucune demande en attente.' : 'Aucune demande traitée.'}
            </p>
          ) : (
            <div className="space-y-2">
              {shownRequests.map(r => {
                const badge = requestTypeBadge(r);
                return (
                  <div key={r.id} className="flex flex-col sm:flex-row sm:items-center gap-2 p-3 rounded-lg bg-white border" data-testid={`request-row-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-gray-800">{r.student_name || r.student_number || 'Étudiant'}</span>
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${badge.cls}`}>{badge.label}</span>
                        {r.status === 'approved' && <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-green-100 text-green-700">Approuvée</span>}
                        {r.status === 'refused' && <span className="text-[11px] px-2 py-0.5 rounded-full font-medium bg-rose-100 text-rose-700">Refusée</span>}
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">{r.target_title} • {new Date(r.created_at).toLocaleString('fr-CA')}</p>
                      {r.comment && <p className="text-xs text-gray-500 italic mt-1">« {r.comment} »</p>}
                      {r.status === 'refused' && r.refusal_reason && <p className="text-xs text-rose-600 mt-1">Raison : {r.refusal_reason}</p>}
                    </div>
                    <div className="flex gap-1.5 flex-shrink-0">
                      {r.status === 'pending' ? (
                        <>
                          <button
                            onClick={() => approveRequest(r)}
                            disabled={processingId === r.id}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg text-white disabled:opacity-50"
                            style={{ backgroundColor: '#24B219' }}
                            data-testid={`approve-request-${r.id}`}
                          >
                            {processingId === r.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                            Approuver et générer
                          </button>
                          <button
                            onClick={() => refuseRequest(r)}
                            disabled={processingId === r.id}
                            className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg border border-rose-300 text-rose-600 hover:bg-rose-50 disabled:opacity-50"
                            data-testid={`refuse-request-${r.id}`}
                          >
                            <X className="w-3.5 h-3.5" /> Refuser
                          </button>
                        </>
                      ) : r.status === 'approved' && (
                        <button
                          onClick={() => navigate(viewPath(r))}
                          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg text-white"
                          style={{ backgroundColor: '#2E6CF5' }}
                          data-testid={`view-request-result-${r.id}`}
                        >
                          <Eye className="w-3.5 h-3.5" /> Voir
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Global grid */}
      <Card className="glass border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg" style={{ color: '#002768' }}>
            <Target className="w-5 h-5" /> Grille d'évaluation globale
          </CardTitle>
          <CardDescription>
            Critères et pondérations utilisés par l'IA pour évaluer automatiquement les sessions (Simulateur 911 ambulance + module Santé). La somme des pondérations doit être 100 %.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <CriteriaEditor criteria={criteria} setCriteria={setCriteria} testPrefix="global" />
          <button
            onClick={saveGlobal}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
            style={{ backgroundColor: '#002768' }}
            data-testid="save-global-grid-btn"
          >
            <Save className="w-4 h-4" /> Sauvegarder la grille globale
          </button>
        </CardContent>
      </Card>

      {/* Overrides */}
      <Card className="glass border-0">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg" style={{ color: '#002768' }}>
            <Layers className="w-5 h-5" /> Grilles personnalisées (par scénario ou protocole)
          </CardTitle>
          <CardDescription>
            Surchargez la grille globale pour un scénario ambulance ou un déterminant MPDS précis.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {overrideCriteria === null ? (
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                value={overrideTarget}
                onChange={e => setOverrideTarget(e.target.value)}
                className="flex-1 px-3 py-2 text-sm border rounded-lg bg-white"
                data-testid="override-target-select"
              >
                <option value="">— Choisir un scénario ou un protocole —</option>
                <optgroup label="Scénarios ambulance">
                  {scenarios.map(s => <option key={s.id} value={s.id}>{s.title}</option>)}
                </optgroup>
                <optgroup label="Protocoles Santé (MPDS)">
                  {protocols.map(p => <option key={p.id} value={p.id}>{p.title}</option>)}
                </optgroup>
              </select>
              <button
                onClick={startOverride}
                className="flex items-center gap-1 px-4 py-2 rounded-lg text-white text-sm font-medium"
                style={{ backgroundColor: '#2E6CF5' }}
                data-testid="create-override-btn"
              >
                <Plus className="w-4 h-4" /> Personnaliser
              </button>
            </div>
          ) : (
            <div className="p-4 rounded-xl border-2 border-blue-200 bg-blue-50/50 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold" style={{ color: '#002768' }}>
                  Grille pour : {findTarget(overrideTarget).label}
                </p>
                <button onClick={() => { setOverrideCriteria(null); setOverrideTarget(''); }} className="p-1 rounded hover:bg-white" data-testid="cancel-override-btn">
                  <X className="w-4 h-4 text-gray-500" />
                </button>
              </div>
              <CriteriaEditor criteria={overrideCriteria} setCriteria={setOverrideCriteria} testPrefix="override" />
              <button
                onClick={saveOverride}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50"
                style={{ backgroundColor: '#002768' }}
                data-testid="save-override-btn"
              >
                <Save className="w-4 h-4" /> Sauvegarder cette grille
              </button>
            </div>
          )}

          {overrides.length > 0 && (
            <div className="space-y-2">
              {overrides.map(o => (
                <div key={o.target_id} className="flex items-center justify-between p-3 rounded-lg bg-white border" data-testid={`override-row-${o.target_id}`}>
                  <div>
                    <p className="text-sm font-medium text-gray-800">{o.target_label}</p>
                    <p className="text-xs text-gray-500">{o.target_type === 'protocol' ? 'Protocole MPDS' : 'Scénario ambulance'} • {o.criteria.length} critères</p>
                  </div>
                  <div className="flex gap-1">
                    <button
                      onClick={() => { setOverrideTarget(o.target_id); setOverrideCriteria(o.criteria.map(c => ({ ...c }))); }}
                      className="px-3 py-1.5 text-xs font-medium rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50"
                      data-testid={`edit-override-${o.target_id}`}
                    >
                      Modifier
                    </button>
                    <button
                      onClick={() => deleteOverride(o.target_id)}
                      className="p-1.5 rounded-lg text-rose-500 hover:bg-rose-50"
                      data-testid={`delete-override-${o.target_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Generated evaluations */}
      <Card className="glass border-0">
        <CardHeader>
          <CardTitle className="text-lg" style={{ color: '#002768' }}>Évaluations générées ({evaluations.length})</CardTitle>
          <CardDescription>Les 200 évaluations automatiques les plus récentes.</CardDescription>
        </CardHeader>
        <CardContent>
          {evaluations.length === 0 ? (
            <p className="text-sm text-gray-500" data-testid="no-evaluations-msg">Aucune évaluation générée pour l'instant.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b">
                    <th className="py-2 pr-3">Date</th>
                    <th className="py-2 pr-3">Étudiant</th>
                    <th className="py-2 pr-3">Session</th>
                    <th className="py-2 pr-3">Type</th>
                    <th className="py-2 pr-3">Score</th>
                    <th className="py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {evaluations.map(ev => (
                    <tr key={ev.id} className="border-b last:border-0 hover:bg-white/60" data-testid={`evaluation-row-${ev.id}`}>
                      <td className="py-2 pr-3 text-xs text-gray-600 whitespace-nowrap">{new Date(ev.created_at).toLocaleString('fr-CA')}</td>
                      <td className="py-2 pr-3 font-medium text-gray-800">{ev.student_name || '—'}</td>
                      <td className="py-2 pr-3 text-gray-700">{ev.target_title}</td>
                      <td className="py-2 pr-3">
                        <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${ev.session_type === 'health' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                          {ev.session_type === 'health' ? 'Santé' : 'Simulateur 911'}
                        </span>
                      </td>
                      <td className="py-2 pr-3">
                        <span className="font-bold" style={{ color: ev.score_global >= 70 ? '#16A34A' : ev.score_global >= 50 ? '#F59E0B' : '#DC2626' }}>
                          {ev.score_global}
                        </span>
                        <span className="text-xs text-gray-400"> /100</span>
                      </td>
                      <td className="py-2">
                        <button
                          onClick={() => navigate(`/auto-evaluation/${ev.session_type}/${ev.session_id}`)}
                          className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-lg text-white"
                          style={{ backgroundColor: '#2E6CF5' }}
                          data-testid={`view-evaluation-${ev.id}`}
                        >
                          <Eye className="w-3.5 h-3.5" /> Voir
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default EvaluationGridTab;
